import { useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { format } from 'date-fns'
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface PointRef { id: number; name: string }

interface MeasurementHistoryModalProps {
  isOpen: boolean
  point: PointRef | null
  token: string | null
  onClose: () => void
  onChanged?: () => void
  canManage?: boolean
  onMove?: () => void
  onEdit?: () => void
  onDeleted?: () => void
}

type RangeKey = '24h' | '7d' | '30d' | '365d'
const RANGES: { key: RangeKey; label: string; hours: number }[] = [
  { key: '24h', label: '24 h', hours: 24 },
  { key: '7d', label: '7 dni', hours: 24 * 7 },
  { key: '30d', label: '30 dni', hours: 24 * 30 },
  { key: '365d', label: 'rok', hours: 24 * 365 },
]

export default function MeasurementHistoryModal({ isOpen, point, token, onClose, onChanged, canManage, onMove, onEdit, onDeleted }: MeasurementHistoryModalProps) {
  const [range, setRange] = useState<RangeKey>('24h')
  const [history, setHistory] = useState<any | null>(null)
  const [alarms, setAlarms] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [minAlarm, setMinAlarm] = useState<string>('')
  const [maxAlarm, setMaxAlarm] = useState<string>('')
  const [savingThresholds, setSavingThresholds] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const authHeaders = { 'Authorization': `Bearer ${token}` }

  const load = async () => {
    if (!point) return
    setLoading(true)
    try {
      const hours = RANGES.find(r => r.key === range)!.hours
      const to = new Date()
      const from = new Date(to.getTime() - hours * 3600 * 1000)
      const qs = `date_from=${encodeURIComponent(from.toISOString())}&date_to=${encodeURIComponent(to.toISOString())}&bucket=auto`
      const [hRes, aRes, pRes] = await Promise.all([
        fetch(`/api/measurement-points/${point.id}/history?${qs}`, { headers: authHeaders }),
        fetch(`/api/measurement-points/${point.id}/alarms?limit=15`, { headers: authHeaders }),
        fetch(`/api/measurement-points/${point.id}`, { headers: authHeaders }),
      ])
      setHistory(hRes.ok ? await hRes.json() : null)
      setAlarms(aRes.ok ? await aRes.json() : [])
      if (pRes.ok) {
        const p = await pRes.json()
        setMinAlarm(p.flow_min_alarm != null ? String(p.flow_min_alarm) : '')
        setMaxAlarm(p.flow_max_alarm != null ? String(p.flow_max_alarm) : '')
      }
    } catch {
      setHistory(null); setAlarms([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && point) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, point?.id, range])

  const labelFmt = (bucket: string) =>
    bucket === 'day' ? 'dd.MM' : bucket === 'hour' ? 'dd.MM HH:00' : 'dd.MM HH:mm'

  const chart = useMemo(() => {
    if (!history?.series?.length) return null
    const fmt = labelFmt(history.bucket)
    const labels = history.series.map((s: any) => { try { return format(new Date(s.ts), fmt) } catch { return s.ts } })
    const flow = history.series.map((s: any) => (s.flow ?? s.flow_avg ?? null))
    const meter = history.series.map((s: any) => (s.meter ?? null))
    return { labels, flow, meter }
  }, [history])

  if (!isOpen || !point) return null

  const saveThresholds = async () => {
    setSavingThresholds(true)
    try {
      const body: any = {
        flow_min_alarm: minAlarm === '' ? -1 : parseFloat(minAlarm),
        flow_max_alarm: maxAlarm === '' ? -1 : parseFloat(maxAlarm),
      }
      const r = await fetch(`/api/measurement-points/${point.id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (r.ok) { onChanged?.(); load() }
    } finally {
      setSavingThresholds(false)
    }
  }

  const handleDelete = async () => {
    if (!point) return
    if (!window.confirm(`Usunąć punkt pomiarowy „${point.name}"?\n\nTa operacja jest nieodwracalna i usunie także jego historię odczytów.`)) return
    setDeleting(true)
    try {
      const r = await fetch(`/api/measurement-points/${point.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      })
      if (r.ok) {
        onDeleted?.()
        onClose()
      } else {
        const err = await r.json().catch(() => ({}))
        alert(err.detail || 'Nie udało się usunąć punktu.')
      }
    } catch {
      alert('Wystąpił błąd podczas usuwania punktu.')
    } finally {
      setDeleting(false)
    }
  }

  const baseOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { ticks: { maxTicksLimit: 8, font: { size: 10 } } }, y: { ticks: { font: { size: 10 } }, beginAtZero: true } },
  } as any

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate pr-2">📊 {point.name} — historia</h2>
          <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold shrink-0">✕</button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {/* Zakres */}
          <div className="flex items-center gap-2 flex-wrap">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`min-h-[36px] px-3 rounded-lg text-sm font-medium transition-colors ${range === r.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {r.label}
              </button>
            ))}
            {history?.consumption != null && (
              <span className="ml-auto text-sm text-gray-600">Zużycie: <strong className="text-gray-900">{history.consumption.toFixed(1)} m³</strong></span>
            )}
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              Ładowanie…
            </div>
          ) : !chart ? (
            <p className="py-8 text-center text-gray-500 text-sm">Brak danych w wybranym zakresie.</p>
          ) : (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Przepływ [m³/h]</p>
                <div className="h-48">
                  <Line
                    data={{ labels: chart.labels, datasets: [{ data: chart.flow, borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.1)', fill: true, tension: 0.25, pointRadius: 0, borderWidth: 2 }] }}
                    options={baseOpts}
                  />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Stan wodomierza [m³]</p>
                <div className="h-40">
                  <Line
                    data={{ labels: chart.labels, datasets: [{ data: chart.meter, borderColor: '#0891b2', backgroundColor: 'rgba(8,145,178,0.08)', fill: true, tension: 0.15, pointRadius: 0, borderWidth: 2 }] }}
                    options={{ ...baseOpts, scales: { ...baseOpts.scales, y: { ticks: { font: { size: 10 } }, beginAtZero: false } } }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Progi alarmowe */}
          <div className="rounded-xl border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-800 mb-2">Progi alarmowe przepływu [m³/h]</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Min (alarm poniżej)</label>
                <input type="number" step="0.1" min="0" value={minAlarm} onChange={e => setMinAlarm(e.target.value)}
                  placeholder="—" className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Max (alarm powyżej)</label>
                <input type="number" step="0.1" min="0" value={maxAlarm} onChange={e => setMaxAlarm(e.target.value)}
                  placeholder="—" className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <button onClick={saveThresholds} disabled={savingThresholds}
              className="mt-2 min-h-[36px] px-3 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {savingThresholds ? 'Zapisywanie…' : 'Zapisz progi'}
            </button>
            <p className="mt-1 text-[11px] text-gray-400">Puste pole = brak progu. Alarm ustawia się przy najbliższym odczycie.</p>
          </div>

          {/* Ostatnie alarmy */}
          {alarms.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Ostatnie zdarzenia alarmowe</p>
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden text-sm">
                {alarms.map(a => (
                  <li key={a.id} className="px-3 py-2 flex items-center justify-between gap-2">
                    <span className={a.state === 'raised' ? 'text-red-600' : 'text-green-600'}>
                      {a.state === 'raised' ? '🔴' : '🟢'} {a.alarm_type === 'high_flow' ? 'Przepływ za wysoki' : 'Przepływ za niski'}
                    </span>
                    <span className="text-gray-400 text-xs shrink-0">{a.created_at ? new Date(a.created_at).toLocaleString('pl-PL') : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          {canManage && (
            <>
              <button onClick={onMove} className="min-h-[44px] px-3 py-2 text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg">📍 Przenieś</button>
              <button onClick={onEdit} className="min-h-[44px] px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">✏️ Edytuj</button>
              <button onClick={handleDelete} disabled={deleting} className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg disabled:opacity-50">
                {deleting ? 'Usuwanie…' : '🗑 Usuń'}
              </button>
            </>
          )}
          <button onClick={onClose} className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg ml-auto">Zamknij</button>
        </div>
      </div>
    </div>
  )
}
